import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, UserRound, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const links = [
  { to: "/woningen", key: "nav.woningen" },
  { to: "/woningwaarde", key: "nav.woningwaarde" },
  { to: "/scam-check", key: "nav.scamcheck" },
  { to: "/prijzen", key: "nav.prijzen" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-border">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/woningalert-logo.png" alt="WoningAlert" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-brandblue-light text-brandblue" : "text-slate-600 hover:text-navy"
                )
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
          <NavLink
            to="/premium"
            data-testid="nav-premium"
            className={({ isActive }) =>
              cn(
                "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
                user?.is_premium
                  ? isActive
                    ? "bg-navy text-amber-300 ring-1 ring-amber-300/50"
                    : "bg-amber-50 text-amber-700 ring-1 ring-amber-300/60 hover:bg-amber-100"
                  : isActive
                    ? "bg-brandblue-light text-brandblue"
                    : "text-slate-600 hover:text-navy"
              )
            }
          >
            <Sparkles size={13} className={user?.is_premium ? "text-amber-500" : ""} />
            {t("nav.premium")}
          </NavLink>
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <button
            data-testid="lang-switch"
            onClick={() => setLang(lang === "nl" ? "en" : "nl")}
            className="flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold text-slate-600 hover:text-navy"
          >
            <Globe size={16} />
            {lang.toUpperCase()}
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="nav-account">
                  <UserRound size={16} className="mr-2" /> {t("nav.account")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-dashboard">{t("nav.dashboard")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/premium" data-testid="menu-premium" className="inline-flex items-center gap-1.5">
                    <Sparkles size={13} className={user.is_premium ? "text-amber-500" : "text-slate-400"} />
                    {t("nav.premium")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-slate-400">{t("menu.section.tenant")}</DropdownMenuLabel>
                <DropdownMenuItem asChild><Link to="/profiel" data-testid="menu-profiel">{t("menu.profiel")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/alerts" data-testid="menu-alerts">{t("menu.alerts")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/reacties" data-testid="menu-reacties">{t("menu.reacties")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/bezichtigingen" data-testid="menu-bezichtigingen">{t("menu.bezichtigingen")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/vault" data-testid="menu-vault">{t("menu.vault")}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/referrals" data-testid="menu-referrals">{t("menu.referrals")}</Link></DropdownMenuItem>
                {user.role === "makelaar" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-slate-400">{t("menu.section.broker")}</DropdownMenuLabel>
                    <DropdownMenuItem asChild><Link to="/makelaar" data-testid="menu-makelaar">{t("menu.makelaar")}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/koop/makelaar" data-testid="menu-koop-makelaar">Koopwoningen (makelaar)</Link></DropdownMenuItem>
                  </>
                )}
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-slate-400">Admin</DropdownMenuLabel>
                    <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin">Admin dashboard</Link></DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/instellingen" data-testid="menu-instellingen">{t("menu.instellingen")}</Link></DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="nav-logout" className="text-danger focus:text-danger">
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" className="rounded-full">
                <Link to="/login" data-testid="nav-login">
                  {t("nav.login")}
                </Link>
              </Button>
              <Button asChild className="rounded-full bg-brandblue hover:bg-brandblue/90">
                <Link to="/register" data-testid="nav-register">
                  {t("nav.register")}
                </Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-white px-4 py-3 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t(l.key)}
              </NavLink>
            ))}
            <Link
              to="/premium"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-premium"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold",
                user?.is_premium ? "text-amber-700" : "text-slate-700"
              )}
            >
              <Sparkles size={14} className={user?.is_premium ? "text-amber-500" : ""} />
              {t("nav.premium")}
            </Link>
            <div className="my-2 h-px bg-border" />
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">
                  {t("nav.dashboard")}
                </Link>
                <Link to="/profiel" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.profiel")}</Link>
                <Link to="/alerts" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.alerts")}</Link>
                <Link to="/reacties" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.reacties")}</Link>
                <Link to="/bezichtigingen" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.bezichtigingen")}</Link>
                <Link to="/vault" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.vault")}</Link>
                <Link to="/referrals" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.referrals")}</Link>
                {user.role === "makelaar" && (
                  <Link to="/makelaar" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.makelaar")}</Link>
                )}
                <Link to="/instellingen" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">{t("menu.instellingen")}</Link>
                <button onClick={doLogout} className="rounded-lg px-3 py-2 text-left text-sm font-medium text-danger">
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium">
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="rounded-lg bg-brandblue px-3 py-2 text-center text-sm font-semibold text-white"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
            <button
              onClick={() => setLang(lang === "nl" ? "en" : "nl")}
              className="mt-2 flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600"
            >
              <Globe size={16} /> {lang.toUpperCase()}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
